import { type QueryClient } from '@tanstack/query-core';
import { log } from '@hungryroot/o11y';
import {
  type Experiment,
  type ExperimentsResponse,
} from '@hungryroot/rest/api/v2/experiments/get';
import { getAnonymousId, trackExperimentEnrollment } from '@hungryroot/segment';
import {
  ExperimentDefinitions,
  type ExperimentVariants,
  type Experiments,
} from '../experiments';
import {
  experimentDefinitionsKey,
  fetchExperimentDefinitions,
} from '../hooks/useExperimentDefinitions';
import { getPersistedEnrollment, persistedEnrollment } from './persist';

function selectRandom<T extends number>(arr: readonly T[]): T {
  const { length } = arr;
  if (length === 1) {
    return arr[0];
  }

  const index = Math.floor(Math.random() * length);
  return arr[index];
}

function findExperimentDefinitionFromServerResponse<T extends Experiments>(
  exp: T,
  resp: ExperimentsResponse
) {
  const { results } = resp;

  let match: ExperimentsResponse['results'][number] | undefined;

  results.forEach((result) => {
    const { id } = result;
    const expId = exp * 100;
    if (id >= expId && id <= expId + 99) {
      match = result;
    }
  });

  if (match === undefined) {
    return null;
  }

  const { variants, id: experimentId, control_weight: controlWeight } = match;
  if (variants === null) {
    return null;
  }

  return {
    serverDefinition: match,
    experimentId,
    controlWeight,
    variants: variants
      .split(',')
      .map((variantStr) => parseInt(variantStr, 10))
      .filter((value) => {
        return isNaN(value) === false;
      }) as ExperimentVariants<T>[],
  };
}

function concurrent<T extends Experiments>(
  expId: T,
  child: () => Promise<ExperimentVariants<T> | null>
) {
  return async () => {
    return new Promise<ExperimentVariants<T> | null>(async (res) => {
      const queue = enrollmentQueue[expId];

      // Handle enrollment requests that are initiated
      // while we are asynchronously enrolling a user
      // We want to queue the resolve callback until
      // after the enrollment has happened ONCE
      if (queue !== undefined) {
        queue.push(res);
        return;
      }

      const pendingEnrollmentQueue = (enrollmentQueue[expId] = [res]);

      const result = await child();
      flushQueue(expId, result, pendingEnrollmentQueue);
      enrollmentQueue[expId] = undefined;
    });
  };
}

type OnEnrollmentResolve<T extends Experiments> = (
  variant: ExperimentVariants<T> | null
) => void;

let enrollmentQueue: Partial<
  Record<Experiments, OnEnrollmentResolve<Experiments>[]>
> = {};

function flushQueue<T extends Experiments>(
  exp: T,
  variant: ExperimentVariants<T> | null,
  callbacks: OnEnrollmentResolve<Experiments>[]
) {
  callbacks.forEach((callback) => callback(variant));
  enrollmentQueue[exp] = undefined;
}

async function queryExperimentDefinitions(queryClient: QueryClient) {
  const queryParams = {
    limit: 1000,
  };

  return await queryClient.fetchQuery({
    queryKey: experimentDefinitionsKey(queryParams),
    queryFn: async () => {
      return fetchExperimentDefinitions(queryParams);
    },
    staleTime: 5 * 60 * 1000,
    retry: 0,
    gcTime: 15 * 60 * 1000,
  });
}

// exporting this to get some unit test coverage
// we can't really test enrollment here because enrollment
// is non-deterministic.
// Want to validate that we are populating the array correctly
export function getWeightedVariants<T extends Experiments>(
  variants: ExperimentVariants<T>[],
  controlWeight: Experiment['control_weight']
) {
  if (controlWeight === null) {
    // no control weight, even split
    return variants;
  }

  const controlValue = variants.find((variant) => variant === 0);

  if (controlValue === undefined) {
    // there is no control
    // return the variants
    return variants;
  }

  // if the controlWeight is 100
  // just return the control, don't bother expanding
  if (controlWeight === 100) {
    return [controlValue];
  }

  // variants will only be 0, 1, 2, 3, etc
  const numVariants = variants.length - 1;
  const variantWeight = Math.round((100 - controlWeight) / numVariants);

  let weightedVariants: ExperimentVariants<T>[] = [];

  for (let i = 0, len = variants.length; i < len; i += 1) {
    const variant = variants[i];
    const arrayLen = variant === controlValue ? controlWeight : variantWeight;
    weightedVariants = weightedVariants.concat(
      Array.from(
        {
          length: arrayLen,
        },
        () => variant
      )
    );
  }

  return weightedVariants;
}

/**
 *
 * This function takes an experiment id (and other things!)
 * and resolves the user's enrollment state. It is designed to be called within a React Hook.
 * The React Hook should understand how to read and write cookies, access React Query query client.
 * The hook MUST pass down those functions here for consumption.
 *
 * This function MUST ALWAYS return synchronously. The return value is how downstream
 * callers will know which state the user is in and what actions they need to perform.
 *
 * The following are the user enrollment states.
 *
 *
 * OPTED OUT
 * The user is in an OPTED OUT state when there enrollment value is currently less than 0.
 * When a user is in this state, we return
 * the variant SYNCHRONOUSLY to allow downstream code to render as quickly as possible.
 *
 * ENROLLED
 * In this state, we have an experiment variant for the user in local cookies, but we have
 * to verify that the experiment itself has not been rev'd on the backend. In this state, we SYNCHRONOUSLY return
 * a tuple containing the variant stored in cookies alongside a function called "checkRevvedExperiment". "checkRevvedExperiment" is
 * called by downstream consumers. It emits a new variant if needed based on how the experiment has been rev'd
 *
 * NOT_ENROLLED
 * Is this state, the user has not been enrolled. We do not have a value in cookies. We have to go to
 * the server to figure out what variants are possible.
 *
 */

export function getExperimentEnrollment<T extends Experiments>({
  expId,
  queryClient,
  env,
}: {
  expId: T;
  queryClient: QueryClient;
  env: {
    isEmbeddedWebView: boolean;
  };
}) {
  const cookieValue = getPersistedEnrollment(expId);

  function enroll(experimentId: number, variant: number) {
    // Log it to segment
    trackExperimentEnrollment({
      experiment: experimentId,
      variant,
    });

    persistedEnrollment(experimentId, variant);
  }

  // User has an experiment enrollment already
  // in cookies. Use that.
  if (cookieValue !== undefined) {
    const variant = cookieValue.variant as ExperimentVariants<T>;

    if (variant < 0) {
      return {
        state: 'opted-out',
        variant,
      } as const;
    }

    return {
      state: 'enrolled',
      variant,
    } as const;
  }

  return {
    state: 'not-enrolled',
    enroll: concurrent(expId, async function enrollExperimentAsync() {
      // Load Experiment Definitions from API
      const enrollmentData = await queryExperimentDefinitions(queryClient);

      // Pull the relevant experiment definitions from the server response
      const def = findExperimentDefinitionFromServerResponse(
        expId,
        enrollmentData
      );

      // We can't find a definition. This may happen in code that
      // is trying to access the experiment before it's been added to the database
      // In this case, we cant resolve the variant (we don't know what they are)
      // So we emit null.
      // The result is _not_ persisted to cookies
      if (def === null) {
        return null;
      }

      const { serverDefinition, variants, controlWeight } = def;

      const { is_active: isActive, is_pre_signup: isPreSignup } =
        serverDefinition;

      if (isPreSignup === false) {
        // Somehow, a post signup experiment has made it here
        // This can happen if an anonymous user is able
        // to unintentionally access a page that should only
        // be visible to logged in users

        // Log that this happened then return null
        // Unclear what to do here.
        // The error is actually that an anonymous user got here
        // in the first place.
        log('experiments.enrollment.post-signup-experiment', {
          experiment: expId,
          serverDefinition,
        });

        return null;
      }

      const { length: variantsLength } = variants;
      const notAbleToDetermineVariant =
        isActive === false || variantsLength === 0;

      // We found an experiment definition, but it's set to inactive or there are no
      // variants defined.
      // In this case, we don't know how to enroll the user so we set the variant to 0
      // The result is _not_ persisted to cookies
      if (notAbleToDetermineVariant === true) {
        return null;
      }

      const { exclude: localExclude } = ExperimentDefinitions[expId];

      const weightedVariants = getWeightedVariants(variants, controlWeight);
      const excludeVariant = await localExclude?.(env);
      // Enrolling a user!
      const variant =
        excludeVariant === undefined
          ? selectRandom(weightedVariants)
          : excludeVariant;

      // Log it to Datadog
      log('experiments.enrollment', {
        experiment: expId,
        // log segment anonymous user id
        // so we can tie these to segment tables
        anonymousSegmentUserId: await getAnonymousId(),
        // stringifying these values so they show up
        // property in datadog
        variant: variant === null ? 'null' : variant,
        serverDefinition:
          serverDefinition === undefined ? 'undefined' : serverDefinition,
      });

      enroll(serverDefinition.id, variant);

      return variant;
    }),
  } as const;
}

export function overrideExperimentVariant({
  overrides,
}: {
  overrides: Array<{
    expId: number;
    variant: number;
  }>;
}) {
  overrides.forEach(({ expId, variant }) => {
    const normalizedExperimentId = expId * 100;

    // persist the cookie
    persistedEnrollment(normalizedExperimentId, variant);
  });
}

export function reset() {
  enrollmentQueue = {};
}
